import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip, IconButton } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye } from '@fortawesome/free-solid-svg-icons'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ExpandableText from '../../ExpandableText'
import { gsap } from 'gsap'

import { ResponsiveTable } from '../../TableComponents'

import styles from './CallLogTable.module.scss'

const CallLogTableView = ({
  rowsList,
  voteStates,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  limit,
  onLimitChange,
  limitOptions
}) => {
  const { t } = useTranslation()

  const prevVoteStatesRef = useRef({})

  useEffect(() => {
    Object.entries(voteStates).forEach(([callId, voteState]) => {
      const prevVote = prevVoteStatesRef.current[callId]

      if (prevVote !== voteState) {
        const voteCell = document.querySelector(`.vote-cell-${callId}`)

        if (voteCell) {
          gsap.killTweensOf(voteCell)

          gsap.fromTo(
            voteCell,
            {
              backgroundColor:
                voteState === 'up'
                  ? 'rgba(63, 81, 181, 0.1)'
                  : voteState === 'down'
                    ? 'rgba(245, 0, 87, 0.1)'
                    : 'transparent'
            },
            {
              backgroundColor: 'transparent',
              duration: 1.2,
              ease: 'power2.out'
            }
          )

          const voteIcon = voteCell.querySelector('svg')
          if (voteIcon) {
            gsap.fromTo(
              voteIcon,
              { scale: 0, rotation: voteState === 'up' ? -20 : 20 },
              {
                scale: 1,
                rotation: 0,
                duration: 0.5,
                ease: 'back.out(1.7)'
              }
            )
          }
        }
      }
    })

    prevVoteStatesRef.current = { ...voteStates }
  }, [voteStates])

  const columns = [
    {
      id: 'details',
      label: '',
      align: 'center',
      renderCell: (row) => (
        <Tooltip title={t('calls.viewDetails')}>
          <IconButton
            className={styles.detailsButton}
            size='small'
            aria-label={t('calls.viewDetails')}
            onClick={(e) => {
              e.stopPropagation()
              onRowClick(row)
            }}
          >
            <FontAwesomeIcon icon={faEye} size='sm' />
          </IconButton>
        </Tooltip>
      )
    },
    {
      id: 'timestamp',
      label: 'Date',
      renderCell: (row) => row[0]
    },
    {
      id: 'direction',
      label: 'Dir.',
      headerCellProps: { title: 'Direction' },
      renderCell: (row) => row[4] === 'Manual' ? t('calls.outbound') : row[4]
    },
    {
      id: 'from',
      label: 'From',
      renderCell: (row) => row[4] === 'Manual' ? row[5] : row[1]
    },
    {
      id: 'to',
      label: 'To',
      renderCell: (row) => row[4] === 'Inbound' ? row[5] : row[1]
    },
    {
      id: 'customerName',
      label: 'Name',
      headerCellProps: { title: 'Customer Name' },
      renderCell: (row) => row[17]
    },
    {
      id: 'customerCompany',
      label: 'Company',
      headerCellProps: { title: 'Customer Company' },
      renderCell: (row) => row[18]
    },
    {
      id: 'agentName',
      label: 'Agent',
      headerCellProps: { title: 'Agent Name' },
      renderCell: (row) => row[6]
    },
    {
      id: 'email',
      label: 'Email',
      columnClassName: styles.hideBelowXl,
      style: { maxWidth: '200px', wordBreak: 'break-word' },
      renderCell: (row) => row[19] || row.caller_email || '—'
    },
    {
      id: 'callType',
      label: 'Type',
      headerCellProps: { title: 'Call Type / Disposition' },
      renderCell: (row) => row[13]
    },
    {
      id: 'handleTime',
      label: 'Time',
      headerCellProps: { title: 'Handle Time (talk + after-call work)' },
      renderCell: (row) => row[9]
    },
    {
      id: 'callNote',
      label: 'Note',
      headerCellProps: { title: 'Call Note' },
      cellClassName: styles.callNoteCell,
      style: { maxWidth: '250px' },
      renderCell: (row) => (
        <ExpandableText text={row[14]} minWidth='200px' />
      )
    },
    {
      id: 'rating',
      label: '',
      headerCellProps: { title: 'Rating' },
      renderCell: (row) => (
        <div
          className={`vote-cell-${row[3]} ${styles.voteCell}`}
        >
          {voteStates[row[3]] === 'up'
            ? <ThumbUpIcon color='primary' fontSize='small' />
            : voteStates[row[3]] === 'down'
              ? <ThumbDownIcon color='secondary' fontSize='small' />
              : null}
        </div>
      )
    }
  ]

  return (
    <ResponsiveTable
      columns={columns}
      data={rowsList}
      onRowClick={onRowClick}
      variant='callLog'
      page={page}
      totalPages={totalPages}
      onPageChange={onPageChange}
      limit={limit}
      onLimitChange={onLimitChange}
      limitOptions={limitOptions}
      containerProps={{
        className: styles.tableContainer
      }}
      tableProps={{
        className: styles.table,
        'aria-label': 'call info table'
      }}
    />
  )
}

export default CallLogTableView
